import {
  ECOMMERCE_ITEM_PRICE_TYPE,
  type EcommerceItemPrice,
  type PlayerUpgrade,
  SPECIAL_ECOMMERCE_ITEM_ID,
} from '@/types/shared/player-config'
import { computed, type ComputedRef, ref } from 'vue'
import { useNow } from '@/composables/game/time'
import { Duration } from 'luxon'
import { useConfig, useUpgradesConfig } from '@/composables/game/config'
import { syncPlayer, usePlayer } from '@/composables/game/player'
import { useEcommerce } from '@/composables/game/ecommerce'
import { formatDuration } from '@/helpers/date'
import { useLocalStorage, watchOnce } from '@vueuse/core'
import { useWalletConnect } from '@/composables/game/wallet-connect'
import { useTonConnectModal } from '@townsquarelabs/ui-vue'
import { buildDropeeStorageKey } from '@/config/config'
import { roundDecimals } from '@/helpers/utils'

export const useUpgradeCooldown = (upgrade: ComputedRef<PlayerUpgrade | null>) => {
  const { now } = useNow()
  const { cardFastForward } = useConfig()
  const { onPurchase, isPurchasing } = useUpgrade()
  const { coins, player } = usePlayer()

  const remainedTime = computed(() => {
    if (!upgrade.value || !upgrade.value.cooldown) return 0
    const timeDiff = upgrade.value.cooldownUntil - now.value
    if (timeDiff < 0) {
      return 0
    }
    return timeDiff
  })

  const formattedTime = computed(() => {
    if (!remainedTime.value) return undefined
    if (remainedTime.value >= 3600) {
      return formatDuration(remainedTime.value)
    }
    return formatDuration(remainedTime.value, 'mm:ss')
  })

  const canFastForward = computed(() => remainedTime.value > cardFastForward.value.minimumCooldown)

  const sortedRanges = computed(() => {
    return cardFastForward.value.ranges
      .filter((range) => range.cooldownUpTo >= remainedTime.value)
      .sort((item1, item2) => item1.cooldownUpTo > item2.cooldownUpTo ? 1 : -1)
  })

  const fastForwardStarPrice = computed<EcommerceItemPrice | undefined>(() => {
    if (sortedRanges.value.length) {
      return sortedRanges.value[0].prices.find((price) => price.type === ECOMMERCE_ITEM_PRICE_TYPE.STAR)
    }
    return undefined
  })

  const fastForwardTonPrice = computed<EcommerceItemPrice | undefined>(() => {
    if (sortedRanges.value.length) {
      return sortedRanges.value[0].prices.find((price) => price.type === ECOMMERCE_ITEM_PRICE_TYPE.TON)
    }
    return undefined
  })

  const fastForwardUsdtPrice = computed<EcommerceItemPrice | undefined>(() => {
    if (sortedRanges.value.length) {
      return sortedRanges.value[0].prices.find((price) => price.type === ECOMMERCE_ITEM_PRICE_TYPE.USDT)
    }
    return undefined
  })

  const fastForwardDropeePrice = computed<EcommerceItemPrice | undefined>(() => {
    if (sortedRanges.value.length) {
      return sortedRanges.value[0].prices.find((price) => price.type === ECOMMERCE_ITEM_PRICE_TYPE.DROPEE)
    }
    return undefined
  })

  const fastForwardPrices: Partial<Record<ECOMMERCE_ITEM_PRICE_TYPE, ComputedRef<EcommerceItemPrice | undefined>>> = {
    [ECOMMERCE_ITEM_PRICE_TYPE.USDT]: fastForwardUsdtPrice,
    [ECOMMERCE_ITEM_PRICE_TYPE.STAR]: fastForwardStarPrice,
    [ECOMMERCE_ITEM_PRICE_TYPE.TON]: fastForwardTonPrice,
    [ECOMMERCE_ITEM_PRICE_TYPE.DROPEE]: fastForwardDropeePrice,
  }

  async function doFastForward(upgradeId: string, priceType: ECOMMERCE_ITEM_PRICE_TYPE) {
    const price = fastForwardPrices[priceType]
    if (!price?.value) {
      return null
    }
    return await onPurchase(upgradeId, price.value, SPECIAL_ECOMMERCE_ITEM_ID.CARD_FAST_FORWARD)
  }

  const formattedEstimatedPurchaseTime = computed(() => {
    if (upgrade.value) {
      const time = ((upgrade.value.price - coins.value) / player.value.profit) * 3600
      const duration = Duration.fromObject({ seconds: time }).shiftTo('hours', 'minutes', 'seconds')
      if (duration.hours > 1) {
        return `~${Math.floor(duration.hours)} hours`
      } else if (duration.minutes > 1) {
        return `~${Math.floor(duration.minutes)} minutes`
      }
      return `~${Math.floor(duration.seconds)} seconds`
    }
    return ''
  })

  return {
    remainedTime,
    formattedTime,
    canFastForward,
    doFastForward,
    fastForwardStarPrice: fastForwardStarPrice,
    fastForwardTonPrice,
    fastForwardUsdtPrice,
    fastForwardDropeePrice,
    formattedEstimatedPurchaseTime,
    isPurchasing,
  }
}

export const useUpgradeExpiration = (upgrade: ComputedRef<PlayerUpgrade | null>) => {
  const { now } = useNow()
  const remainedTime = computed(() => {
    if (!upgrade.value || !upgrade.value?.expiresOn) return 0
    const timeDiff = upgrade.value?.expiresOn - now.value
    if (timeDiff < 0) {
      return 0
    }
    return timeDiff
  })

  const formattedTime = computed(() => {
    if (!remainedTime.value) return undefined
    const oneDay = 24 * 3600
    if (remainedTime.value >= oneDay) {
      const days = Math.floor(remainedTime.value / oneDay)
      return `${days} day${days > 1 ? 's' : ''}`
    }
    return formatDuration(remainedTime.value)
  })

  return {
    remainedTime,
    formattedTime,
  }
}

export const useUpgrade = () => {
  const { purchase } = useEcommerce()
  const { syncConfig, config } = useConfig()
  const selectedPriceType = ref<ECOMMERCE_ITEM_PRICE_TYPE | null>(null)
  const { rawAddress } = useWalletConnect()
  const { open } = useTonConnectModal()
  const isPurchasing = ref(false)

  async function onPurchase(upgradeId: string, ecommercePrice: EcommerceItemPrice, itemId: SPECIAL_ECOMMERCE_ITEM_ID.CARD_UPGRADE | SPECIAL_ECOMMERCE_ITEM_ID.CARD_FAST_FORWARD) {
    selectedPriceType.value = ecommercePrice.type

    try {
      if (ecommercePrice.type === ECOMMERCE_ITEM_PRICE_TYPE.TON && !rawAddress.value) {
        open()

        // Wrap the watchOnce in a Promise
        const waitForAddress = new Promise<PlayerUpgrade | null>((resolve) => {
          watchOnce(rawAddress, async () => {
            if (!rawAddress.value) return resolve(null)

            const playerUpgrade = await purchaseForCard(upgradeId, ecommercePrice, itemId)
            selectedPriceType.value = null
            resolve(playerUpgrade)
          })
        })

        return await waitForAddress
      }

      const playerUpgrade = await purchaseForCard(upgradeId, ecommercePrice, itemId)
      selectedPriceType.value = null
      return playerUpgrade
    } catch (e) {
      selectedPriceType.value = null
      return null
    }
  }

  async function purchaseForCard(upgradeId: string, ecommercePrice: EcommerceItemPrice, itemId: SPECIAL_ECOMMERCE_ITEM_ID.CARD_UPGRADE | SPECIAL_ECOMMERCE_ITEM_ID.CARD_FAST_FORWARD) {
    isPurchasing.value = true
    try {
      const { paid } = await purchase({
        type: 'card',
        upgradeId: upgradeId,
        price: ecommercePrice,
        itemId: itemId,
      })
      if (paid) {
        await syncConfig()
        await syncPlayer()
        return config.value.upgrades.find((upgrade) => upgrade.id === upgradeId) ?? null
      }
      return null
    } catch (e) {
      return null
    } finally {
      isPurchasing.value = false
    }
  }

  return {
    onPurchase,
    isPurchasing,
  }
}

export const useUpgradableCardsCount = () => {
  const { upgrades } = useUpgradesConfig()
  const { player } = usePlayer()
  const { now } = useNow()

  const eligibleUpgrades = computed(() => {
    return upgrades.value.filter(upgrade => {
      const isExpired = !!upgrade.expiresOn && now.value > upgrade.expiresOn
      if (isExpired) return false

      if (upgrade.price === 0) return false // max level

      const isInBattleConflict =
        upgrade.id.includes('/') &&
        upgrades.value.some(u => u.id !== upgrade.id && u.id.includes('/') && u.level > 0)
      if (isInBattleConflict) return false

      const { playerLevel, upgrade: upgradeLock, referrals: referralLock, newReferrals } = upgrade.requirements

      if (playerLevel !== undefined && player.value.level < playerLevel) return false

      const lastReferralTimestamp = player.value.referrals.lastReferral ?? 0
      if (newReferrals?.since && lastReferralTimestamp < newReferrals.since && !upgrade.level) return false

      if (upgradeLock !== undefined && (upgrades.value.find(u => u.id === upgradeLock.id)?.level ?? 0) < upgradeLock.level) return false

      if (referralLock !== undefined && (player.value.friends.count + player.value.friends.virtualCount) < referralLock.count) return false

      if (upgrade.cooldown > 0 && upgrade.cooldownUntil > now.value) return false

      return true
    })
  })

  const greedyResult = computed(() => {
    const sorted = [...eligibleUpgrades.value]
      .sort((a, b) => (b.profitDelta / b.price) - (a.profitDelta / a.price))
    let remaining = player.value.coins
    let coinsSpent = 0
    let profitGained = 0
    let count = 0
    for (const upgrade of sorted) {
      if (remaining >= upgrade.price) {
        remaining -= upgrade.price
        coinsSpent += upgrade.price
        profitGained += upgrade.profitDelta
        count++
      }
    }
    return { coinsSpent, profitGained, count }
  })

  const affordableCount = computed(() => greedyResult.value.count)
  const totalProfitDelta = computed(() => greedyResult.value.profitGained)
  const totalCoinsToSpend = computed(() => greedyResult.value.coinsSpent)

  return { affordableCount, totalProfitDelta, totalCoinsToSpend }
}

export const useCardBattle = () => {
  const { upgrades } = useUpgradesConfig()

  function isInBattleConflict(upgrade: PlayerUpgrade) {
    if (!upgrade.id.includes('/')) return false
    return upgrades.value.some(u => u.id !== upgrade.id && u.id.includes('/') && u.level > 0)
  }

  return {
    isInBattleConflict,
  }
}

export const useNewUpgradesChecker = () => {
  const { upgrades } = useUpgradesConfig()
  const lastInitTimestamp = useLocalStorage<number>(buildDropeeStorageKey('lastInitTimestamp'), 0)

  const newUpgrades = computed(() => {
    const fortyEightHoursInSeconds = 48 * 60 * 60 // equals 172800 seconds
    const cutoffTime = lastInitTimestamp.value - fortyEightHoursInSeconds
    return upgrades.value.filter(upgrade => upgrade.createdAt >= cutoffTime && upgrade.level < 1)
  })

  function isUpgradeNew(upgradeId: string) {
    return newUpgrades.value.some(u => u.id === upgradeId)
  }

  return {
    newUpgrades,
    isUpgradeNew,
  }
}

const batchFastForwardPurchased = ref(false)

export const useBatchFastForward = () => {
  const { upgrades } = useUpgradesConfig()
  const { config } = useConfig()
  const { now } = useNow()
  const priceConfig = computed(() => config.value.game.ecommerce.prices.cardFastForward)
  const minimumCooldown = computed(() => priceConfig.value.minimumCooldown)
  const unfilteredUpgradesWithCooldown = computed(() => upgrades.value.filter(u => u.cooldownUntil - now.value > 0)) //include also the ones BELOW the minimum cooldown
  const upgradesWithCooldown = computed(() => upgrades.value.filter(u => u.cooldown >= minimumCooldown.value && (u.cooldownUntil - now.value > minimumCooldown.value)))
  const ecommerceItem = computed(() => config.value.game.ecommerce.items.find((item) => item.id === SPECIAL_ECOMMERCE_ITEM_ID.CARD_FAST_FORWARD_ALL))
  const canBatchFastForward = computed(() => upgradesWithCooldown.value.length >= priceConfig.value.batchMinQuantity && !!ecommerceItem.value)
  const batchDiscount = computed(() => priceConfig.value.batchDiscount)

  return {
    canBatchFastForward,
    unfilteredUpgradesWithCooldown,
    upgradesWithCooldown,
    batchDiscount,
    ecommerceItem,
    batchFastForwardPurchased,
  }
}

interface TotalCooldownPrices {
  remainedTime: number
  starAmount: number
  tonAmount: number
  dropeeAmount: number
  usdtAmount: number
  starUsdAmount: number
  goldenCoinAmount: number
}

/**
 * Returns a single computed that aggregates the fast-forward prices across all upgrades.
 */
export function useBatchFastForwardsCooldown() {
  const { now } = useNow()
  const { cardFastForward } = useConfig()
  const { upgradesWithCooldown, unfilteredUpgradesWithCooldown, batchDiscount } = useBatchFastForward()

  const totalPrices = computed<TotalCooldownPrices>(() => {
    return upgradesWithCooldown.value.reduce(
      (acc, upg) => {
        if (upg.cooldown) {
          const remained = Math.max(upg.cooldownUntil - now.value, 0)
          const ranges = cardFastForward.value.ranges
            .filter(r => r.cooldownUpTo >= remained)
            .sort((a, b) => a.cooldownUpTo - b.cooldownUpTo)

          const starPrice = ranges[0]?.prices.find(
            p => p.type === ECOMMERCE_ITEM_PRICE_TYPE.STAR,
          )
          const tonPrice = ranges[0]?.prices.find(
            p => p.type === ECOMMERCE_ITEM_PRICE_TYPE.TON,
          )
          const dropeePrice = ranges[0]?.prices.find(
            p => p.type === ECOMMERCE_ITEM_PRICE_TYPE.DROPEE,
          )
          const usdtPrice = ranges[0]?.prices.find(
            p => p.type === ECOMMERCE_ITEM_PRICE_TYPE.USDT,
          )
          const goldenCoinPrice = ranges[0]?.prices.find(
            p => p.type === ECOMMERCE_ITEM_PRICE_TYPE.GOLDEN_COIN,
          )

          acc.remainedTime += remained
          acc.starAmount += starPrice?.amount ?? 0
          acc.tonAmount += tonPrice?.amount ?? 0
          acc.dropeeAmount += dropeePrice?.amount ?? 0
          acc.usdtAmount += usdtPrice?.amount ?? 0
          acc.starUsdAmount += starPrice?.usdAmount ?? 0
          acc.goldenCoinAmount += goldenCoinPrice?.amount ?? 0
        }
        return acc
      },
      { remainedTime: 0, starAmount: 0, tonAmount: 0, dropeeAmount: 0, usdtAmount: 0, starUsdAmount: 0, goldenCoinAmount: 0 } as TotalCooldownPrices,
    )
  })

  const totalUnfilteredRemainedTime = computed(() => unfilteredUpgradesWithCooldown.value.reduce(
    (acc, upg) => {
      acc += Math.max(upg.cooldownUntil - now.value, 0)
      return acc
    },
    0,
  ))

  const discountedTotalPrices = computed(() => {
    const _totalPrices = totalPrices.value
    const discountMultiplier = (100 - batchDiscount.value) / 100

    return {
      remainedTime: _totalPrices.remainedTime,
      starAmount: roundDecimals(_totalPrices.starAmount * discountMultiplier, 0),
      tonAmount: roundDecimals(_totalPrices.tonAmount * discountMultiplier, 2),
      dropeeAmount: roundDecimals(_totalPrices.dropeeAmount * discountMultiplier, 2),
      usdtAmount: roundDecimals(_totalPrices.usdtAmount * discountMultiplier, 2),
      starUsdAmount: roundDecimals(_totalPrices.starUsdAmount * discountMultiplier, 2),
      originalUsdAmount: roundDecimals(_totalPrices.starUsdAmount, 2),
      goldenCoinAmount: roundDecimals(_totalPrices.goldenCoinAmount * discountMultiplier, 0),
    }
  })

  return {
    discountedTotalPrices,
    upgradesWithCooldown,
    unfilteredUpgradesWithCooldown,
    batchFastForwardPurchased,
    totalUnfilteredRemainedTime,
  }
}
